import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  Typography,
} from '@mui/material';
import type { RelatedColumnDescriptor, LookupDataItem, SortDescriptor } from '../../types';
import { useGetDropdownGridDataQuery } from '../../api/gridApi';
import { formatDateValue } from './utils';
import { PaginationControls } from './ui';
import { SearchBar } from '../toolbar/SearchBar';
import { HighlightText } from '../../../../components/common/HighlightText';

/** Matches ISO 8601 date strings like 2024-01-15T10:30:00 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

type SortDirection = 'asc' | 'desc';

type LookupGridPanelProps = {
  gridId: string;
  columns: RelatedColumnDescriptor[];
  usePaging: boolean;
  baseFilter?: unknown;
  staticData?: LookupDataItem[];
  onSelect: (id: string) => void;
};

const storageKey = (gridId: string) => `lookupGridPanel:${gridId}`;

type PersistedState = {
  page: number;
  pageSize: number;
};

const readPersistedState = (gridId: string): PersistedState | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(gridId));
    return raw ? (JSON.parse(raw) as PersistedState) : null;
  } catch {
    return null;
  }
};

const writePersistedState = (gridId: string, state: PersistedState) => {
  try {
    sessionStorage.setItem(storageKey(gridId), JSON.stringify(state));
  } catch {
    console.log(' sessionStorage may be unavailable');
  }
};

export const LookupGridPanel = ({
  gridId,
  columns,
  usePaging,
  baseFilter,
  staticData,
  onSelect,
}: LookupGridPanelProps) => {
  const persisted = useMemo(() => readPersistedState(gridId), [gridId]);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(persisted?.page ?? 0);
  const [pageSize, setPageSize] = useState(persisted?.pageSize ?? 10);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    writePersistedState(gridId, { page, pageSize });
  }, [gridId, page, pageSize]);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setPage(0);
  }, []);

  const handleSort = useCallback((field: string) => {
    setSortField((prevField) => {
      setSortDirection((prevDirection) =>
        prevField === field && prevDirection === 'asc' ? 'desc' : 'asc'
      );
      return field;
    });
    setPage(0);
  }, []);

  // Build server-side filter (OR across all columns)
  const serverFilter = useMemo(() => {
    if (!usePaging || !searchTerm.trim()) return undefined;
    if (columns.length === 0) return undefined;
    const term = searchTerm.trim();
    const parts: unknown[] = [];
    columns.forEach((col, i) => {
      if (i > 0) parts.push('or');
      parts.push([col.dataField, 'contains', term]);
    });
    return parts.length ? parts : undefined;
  }, [searchTerm, columns, usePaging]);

  // Combine baseFilter with serverFilter
  const combinedFilter = useMemo(() => {
    if (baseFilter && serverFilter) {
      return [baseFilter, 'and', serverFilter];
    }
    return baseFilter || serverFilter || undefined;
  }, [baseFilter, serverFilter]);

  const serverSort = useMemo<SortDescriptor[] | undefined>(() => {
    if (!usePaging || !sortField) return undefined;
    return [{ selector: sortField, desc: sortDirection === 'desc' }];
  }, [usePaging, sortField, sortDirection]);

  const skipQuery = !!staticData;

  const queryArgs = usePaging
    ? {
        gridId,
        skip: page * pageSize,
        take: pageSize,
        filter: combinedFilter,
        sort: serverSort,
      }
    : { gridId, skip: 0, take: 10000, filter: baseFilter };

  const { data: fetchedData, isLoading } = useGetDropdownGridDataQuery(queryArgs, {
    skip: skipQuery,
  });

  // Convert static data to grid format (flatten Fields array to column properties)
  const staticGridData = useMemo(() => {
    if (!staticData) return null;

    const flattened = staticData.map((item) => {
      const row: Record<string, unknown> = { Id: item.Id };
      item.Fields.forEach((field) => {
        row[field.Name] = field.StringValue;
      });
      return row;
    });

    return flattened;
  }, [staticData]);

  // Client-side filter + paginate (skip when server already filtered via usePaging)
  const filteredRows = useMemo(() => {
    const sourceData = staticGridData || fetchedData?.data || [];

    if (usePaging || !searchTerm.trim()) return sourceData;

    const term = searchTerm.trim().toLowerCase();
    return sourceData.filter((row) =>
      columns.some((col) =>
        String(row[col.dataField] ?? '')
          .toLowerCase()
          .includes(term)
      )
    );
  }, [staticGridData, fetchedData, searchTerm, columns, usePaging]);

  const sortedRows = useMemo(() => {
    if (usePaging) return filteredRows;
    if (!sortField) return filteredRows;

    const factor = sortDirection === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return -1 * factor;
      if (bValue == null) return 1 * factor;

      const aStr = String(aValue);
      const bStr = String(bValue);
      const aNum = Number(aStr);
      const bNum = Number(bStr);

      if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aStr !== '' && bStr !== '') {
        return (aNum - bNum) * factor;
      }

      return aStr.localeCompare(bStr) * factor;
    });
  }, [filteredRows, sortField, sortDirection, usePaging]);

  // When using static data, always use client-side pagination
  // When using API (usePaging=true), use server-side pagination
  const useClientSidePagination = !!staticData || !usePaging;

  const displayRows = useClientSidePagination
    ? sortedRows.slice(page * pageSize, (page + 1) * pageSize)
    : sortedRows;
  const totalCount = useClientSidePagination ? filteredRows.length : fetchedData?.totalCount || 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 450,
      }}
    >
      <Box sx={{ p: 0.5 }}>
        <SearchBar value={searchTerm} onChange={handleSearchChange} fullWidth size="compact" />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer
          sx={{
            height: 300,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.dataField}
                    sortDirection={sortField === col.dataField ? sortDirection : false}
                    sx={{
                      minWidth: col.minWidth ? parseInt(col.minWidth, 10) : 100,
                      position: 'sticky',
                      top: 0,
                      zIndex: 100,
                      fontWeight: 600,
                      py: 0.5,
                      px: 1.5,
                    }}
                  >
                    <TableSortLabel
                      active={sortField === col.dataField}
                      direction={sortField === col.dataField ? sortDirection : 'asc'}
                      onClick={() => handleSort(col.dataField)}
                    >
                      {col.caption}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : displayRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 0.5 }}
                    >
                      No results
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayRows.map((row, idx) => (
                  <TableRow
                    key={String(row.Id ?? idx)}
                    onClick={() => onSelect(String(row.Id))}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {columns.map((col) => {
                      const value = row[col.dataField];
                      const displayValue =
                        typeof value === 'string' && ISO_DATE_RE.test(value)
                          ? formatDateValue(value)
                          : String(value ?? '');
                      return (
                        <TableCell key={col.dataField} sx={{ py: 0.5, px: 1.5 }}>
                          <HighlightText text={displayValue} highlight={searchTerm} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <PaginationControls
        displayRowCount={totalCount}
        displayPage={page}
        displayPageSize={pageSize}
        handlePageChange={(_event, newPage) => setPage(newPage)}
        handlePageSizeChange={(e) => {
          setPageSize(Number(e.target.value));
          setPage(0);
        }}
        compact
      />
    </Box>
  );
};
